import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { trainerAPI, usersAPI } from '../../api';
import { formatDate } from '../../utils';
import { Card, Button, Select, Modal, Spinner, EmptyState } from '../../components/ui';
import { Users, User, Plus, ChevronRight } from 'lucide-react';

export const TrainerClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');

  const loadClients = useCallback(async () => {
    try {
      const [clientsRes, usersRes] = await Promise.all([
        trainerAPI.getClients(),
        usersAPI.listAvailableClients()
      ]);
      setClients(clientsRes.data);
      setAllUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleAssign = async () => {
    if (!selectedClient) return;
    try {
      await trainerAPI.assignClient(selectedClient);
      setShowModal(false);
      setSelectedClient('');
      loadClients();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to assign client');
    }
  };

  const assignedIds = clients.map(c => c.client_id);
  const availableClients = allUsers.filter(u => !assignedIds.includes(u.id));

  return (
    <div className="space-y-6" data-testid="trainer-clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Clients</h1>
          <p className="text-gray-500">Manage your assigned clients</p>
        </div>
        <Button onClick={() => setShowModal(true)} data-testid="add-client-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Client
        </Button>
      </div>

      <Card>
        {loading ? (
          <div className="p-8 text-center"><Spinner /></div>
        ) : clients.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No clients assigned"
            description="Add clients to start logging their sessions"
            action={<Button onClick={() => setShowModal(true)}>Add Your First Client</Button>}
          />
        ) : (
          <div className="divide-y">
            {clients.map(client => (
              <div key={client.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium">{client.client_name}</p>
                    <p className="text-sm text-gray-500">Added {formatDate(client.created_at)}</p>
                  </div>
                </div>
                  <div className="flex gap-2">
                    <Link to={`/clients/${client.client_id}/report`}>
                      <Button variant="outline" size="sm">
                        View Report
                      </Button>
                    </Link>
                    <Link to={`/sessions?client=${client.client_id}`}>
                      <Button variant="outline" size="sm">
                        Log Session <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Client">
        <div className="space-y-4">
          <Select
            label="Select Client"
            value={selectedClient}
            onChange={e => setSelectedClient(e.target.value)}
            options={[
              { value: '', label: 'Choose a client...' },
              ...availableClients.map(u => ({ value: u.id, label: u.name }))
            ]}
            data-testid="client-select"
          />
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setShowModal(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={!selectedClient} className="flex-1" data-testid="confirm-add-client">
              Add Client
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TrainerClientsPage;
